// client/src/components/MerchantCamera.js

import React, { useState, useEffect, useRef } from 'react';
import { firestore, storage } from '../../firebase';
import {
  doc,
  updateDoc,
  getDoc,
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import '../CSS/Dashboard.css';
import '../CSS/camera.css';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import jsQR from 'jsqr';
import Barcode from 'react-barcode';

const MerchantCamera = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line
  const [imageURL, setImageURL] = useState(null);
  // eslint-disable-next-line
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [validationFailure, setValidationFailure] = useState(false);
  // eslint-disable-next-line
  const [generatedCode, setGeneratedCode] = useState(null);
  // eslint-disable-next-line
  const [showModal, setShowModal] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrAlreadyProcessed, setQrAlreadyProcessed] = useState(false);

  const cameraContainerRef = useRef(null);
  const scanIntervalRef = useRef(null);

  useEffect(() => {
    handleCapturePhoto();

    return () => {
      closeCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeCamera = () => {
    ['camera-container', 'capture-container'].forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        const video = el.querySelector('video');
        if (video && video.srcObject) {
          video.srcObject.getTracks().forEach(t => t.stop());
        }
        el.remove();
      }
    });
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    setIsCameraOpen(false);
  };

  const dataURLToBlob = (dataURL) => {
    const parts = dataURL.split(',');
    if (parts.length !== 2) return null;
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const binary = atob(parts[1]);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
  };

  const processQRCode = async (qrData) => {
    if (qrAlreadyProcessed) return;
    setQrAlreadyProcessed(true);
    setQrLoading(true);

    let parsed = null;
    try { parsed = new URL(qrData); } catch { parsed = null; }

    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error('Utilisateur non authentifié');

      // ====== NOUVEAU : redirection de transfert (recommandé) ======
      // https://.../redirectqrctransfertfid/<offerId>  (ou ?offer=)
      if (parsed && parsed.pathname.replace(/\/+$/,'').startsWith('/redirectqrctransfertfid')) {
        const parts = parsed.pathname.replace(/\/+$/,'').split('/');
        const offerId = parts[2] || parsed.searchParams.get('offer') || '';
        if (!offerId) throw new Error('Offre invalide');
        closeCamera();
        navigate(`/transfertfid?offer=${encodeURIComponent(offerId)}&mode=claim`);
        return;
      }

      // ====== Compat ancien : /transferoffer/<offerId> ======
      if (parsed && parsed.pathname.replace(/\/+$/,'').startsWith('/transferoffer')) {
        const parts = parsed.pathname.replace(/\/+$/,'').split('/');
        const offerId = parts[2] || '';
        if (!offerId) throw new Error('Offre invalide');
        closeCamera();
        navigate(`/transfertfid?offer=${encodeURIComponent(offerId)}&mode=claim`);
        return;
      }

      // ====== Compat ancien : /transferfid?to=<uid> ======
      if (parsed && parsed.pathname.replace(/\/+$/,'') === '/transferfid') {
        const toUid = parsed.searchParams.get('to');
        if (!toUid) throw new Error('QR transfert invalide (paramètre "to" manquant)');
        closeCamera();
        navigate(`/transfertfid?to=${encodeURIComponent(toUid)}`);
        return;
      }

      // ====== Flux EXISTANT marchand : QR entreprise (redirectqrc) ======
      // Accepte UID brut ou https://ecobill.app/redirectqrc/<uid>
      let merchantUid = qrData;
      if (parsed && parsed.hostname === 'ecobill.app' && parsed.pathname.startsWith('/redirectqrc/')) {
        merchantUid = parsed.pathname.split('/').pop();
      }

      // Vérifie fidélité active
      const q = query(
        collection(firestore, 'entreprises'),
        where('userId', '==', merchantUid),
        where('fidelity', '==', 'active')
      );
      const qs = await getDocs(q);
      if (qs.empty) throw new Error('Fidélité inactive ou entreprise introuvable');

      closeCamera();
      // ✅ Navigation directe (suppression de navigate('/merchantinvoices'))
      navigate(`/merchantscanqrc/${encodeURIComponent(merchantUid)}`);
    } catch (err) {
      console.error('Erreur lors du traitement du QR Code :', err);
      setValidationFailure(true);
      setTimeout(() => setValidationFailure(false), 1000);
      const container = cameraContainerRef.current;
      const video = container?.querySelector('video');
      if (video) scanQRCode(video);
    } finally {
      setQrLoading(false);
      setQrAlreadyProcessed(false);
    }
  };

  const scanQRCode = (videoElement) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    scanIntervalRef.current = setInterval(() => {
      if (videoElement.readyState === videoElement.HAVE_ENOUGH_DATA) {
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;
        ctx.drawImage(videoElement, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, canvas.width, canvas.height);
        if (code) {
          clearInterval(scanIntervalRef.current);
          processQRCode(code.data);
        }
      }
    }, 500);
  };

  const handleCapturePhoto = async () => {
    let facingMode = 'environment';
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      setIsCameraOpen(true);

      const container = document.createElement('div');
      container.id = 'camera-container';
      container.className = 'camera-overlay';
      cameraContainerRef.current = container;
      document.body.appendChild(container);

      const videoWrapper = document.createElement('div');
      videoWrapper.className = 'camera-frame';
      container.appendChild(videoWrapper);

      const video = document.createElement('video');
      video.autoplay = true;
      video.playsInline = true;
      video.srcObject = stream;
      video.controls = false;
      video.className = 'camera-feed';
      videoWrapper.appendChild(video);
      await new Promise(res => (video.onloadedmetadata = () => video.play().then(res)));

      scanQRCode(video);

      const bar = document.createElement('div');
      bar.className = 'camera-bottom-bar';
      container.appendChild(bar);

      const createButton = ({ variant, ariaLabel, innerHTML, glyph, onClick }) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `camera-bottom-bar__button ${variant || ''}`.trim();
        if (ariaLabel) button.setAttribute('aria-label', ariaLabel);
        button.innerHTML = innerHTML || `<span class="camera-bottom-bar__icon">${glyph || ''}</span>`;
        button.addEventListener('click', onClick);
        bar.appendChild(button);
        return button;
      };

      createButton({
        variant: 'camera-bottom-bar__button--ghost camera-bottom-bar__button--close',
        ariaLabel: 'Fermer',
        innerHTML: '<span class="camera-bottom-bar__icon">✕</span>',
        onClick: () => {
          closeCamera();
          navigate('/merchantinvoices');
        }
      });

      createButton({
        variant: 'camera-bottom-bar__button--capture',
        ariaLabel: 'Capturer une photo',
        innerHTML: '<span class="camera-bottom-bar__capture"><span></span></span>',
        onClick: () => captureImage(video, stream)
      });

      createButton({
        variant: 'camera-bottom-bar__button--ghost camera-bottom-bar__button--switch',
        ariaLabel: 'Changer de caméra',
        innerHTML: '<span class="camera-bottom-bar__icon">⟳</span>',
        onClick: () => {
          closeCamera();
          handleCapturePhoto();
        }
      });
    } catch (err) {
      console.error('Erreur accès caméra :', err);
      alert('Impossible d\'accéder à la caméra. Vérifiez permissions et contexte sécurisé (HTTPS).');
    }
  };

  const captureImage = (video, stream) => {
    const rect = video.getBoundingClientRect();
    const arVideo = video.videoWidth / video.videoHeight;
    const arVisible = rect.width / rect.height;
    let sx, sy, sw, sh;
    if (arVideo > arVisible) {
      sh = video.videoHeight;
      sw = sh * arVisible;
      sx = (video.videoWidth - sw) / 2;
      sy = 0;
    } else {
      sw = video.videoWidth;
      sh = sw / arVisible;
      sx = 0;
      sy = (video.videoHeight - sh) / 2;
    }

    const canvas = document.createElement('canvas');
    canvas.width = sw; canvas.height = sh;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    if (!dataUrl) return;
    const previewBlob = dataURLToBlob(dataUrl);
    if (!previewBlob) return;

    setImageURL(dataUrl);
    stream.getTracks().forEach(t => t.stop());

    closeCamera();
    const captureContainer = document.createElement('div');
    captureContainer.id = 'capture-container';
    captureContainer.className = 'camera-overlay';
    document.body.appendChild(captureContainer);

    const previewWrapper = document.createElement('div');
    previewWrapper.className = 'camera-frame camera-frame--preview';
    captureContainer.appendChild(previewWrapper);

    const img = document.createElement('img');
    img.src = dataUrl;
    img.alt = 'Aperçu de la capture';
    img.className = 'camera-preview';
    previewWrapper.appendChild(img);

    const actions = document.createElement('div');
    actions.className = 'camera-bottom-bar camera-bottom-bar--overlay';
    captureContainer.appendChild(actions);

    const createBtn = ({ variant, ariaLabel, innerHTML, glyph, onClick }) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `camera-bottom-bar__button ${variant || ''}`.trim();
      if (ariaLabel) button.setAttribute('aria-label', ariaLabel);
      button.innerHTML = innerHTML || `<span class="camera-bottom-bar__icon">${glyph || ''}</span>`;
      button.addEventListener('click', onClick);
      actions.appendChild(button);
      return button;
    };

    createBtn({
      variant: 'camera-bottom-bar__button--ghost camera-bottom-bar__button--close',
      ariaLabel: 'Annuler et revenir aux documents',
      innerHTML: '<span class="camera-bottom-bar__icon">✕</span>',
      onClick: () => {
        captureContainer.remove();
        navigate('/merchantinvoices');
      }
    });

    createBtn({
      variant: 'camera-bottom-bar__button--approve',
      ariaLabel: 'Valider cette photo',
      innerHTML: '<span class="camera-bottom-bar__icon">✔</span>',
      onClick: async () => {
        const success = await handleValidatePhoto(previewBlob);
        if (success) {
          captureContainer.remove();
          navigate('/merchantinvoices');
        } else {
          alert("Erreur lors de l'upload. Veuillez réessayer.");
        }
      }
    });

    createBtn({
      variant: 'camera-bottom-bar__button--ghost camera-bottom-bar__button--switch',
      ariaLabel: 'Reprendre une photo',
      innerHTML: '<span class="camera-bottom-bar__icon">⟳</span>',
      onClick: () => {
        captureContainer.remove();
        handleCapturePhoto();
      }
    });
  };

  const handleValidatePhoto = async (blob) => {
    setUploading(true);
    const user = getAuth().currentUser;
    if (!user) { alert('Utilisateur non authentifié'); setUploading(false); return false; }
    const userId = user.uid;
    const userEmail = user.email;
    try {
      const userDoc = await getDoc(doc(firestore, 'users', userId));
      const userPseudo = userDoc.data()?.pseudo || 'Inconnu';
      const photoPath = `${userId}/photos/${userId}-${Date.now()}.png`;
      const photoRef = ref(storage, photoPath);
      await uploadBytes(photoRef, blob, { customMetadata: { category: 'perso', TakenByEmail: userEmail, TakenByUID: userId, TakenByPseudo: userPseudo } });
      setValidationSuccess(true);
      getDownloadURL(photoRef)
        .then(url => updateDoc(doc(firestore, 'users', userId), { photoURL: url }))
        .catch(console.error);
      window.location.href = '/merchantinvoices';
      return true;
    } catch (err) {
      console.error("Erreur lors de l'upload de la photo :", err);
      alert('Erreur lors de l’enregistrement de la photo.');
      setUploading(false);
      return false;
    }
  };

  const overlayIcon = (color, z = 9999) => ({
    position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 128, height: 128, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '72px', color, zIndex: z,
    background: 'rgba(255, 255, 255, 0.92)', boxShadow: 'var(--eb-shadow-lg)'
  });

  return (
    <div>
      {validationSuccess && (
        <div style={overlayIcon('var(--eb-success)')} role="status" aria-label="Validé">
          <FaCheckCircle />
        </div>
      )}
      {validationFailure && (
        <div style={overlayIcon('var(--eb-danger)')} role="alert" aria-label="Échec">
          <FaTimesCircle />
        </div>
      )}
      {qrLoading && (
        <div style={{ ...overlayIcon('#fff', 10000), background: 'rgba(15, 35, 56, 0.55)', fontSize: '56px' }} role="status" aria-label="Chargement">
          <FaSpinner className="spin" />
        </div>
      )}
      {uploading && (
        <div style={{ ...overlayIcon('#fff', 10000), background: 'rgba(15, 35, 56, 0.55)', fontSize: '56px' }} role="status" aria-label="Envoi en cours">
          <FaSpinner className="spin" />
        </div>
      )}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 35, 56, 0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16, zIndex: 10000 }}>
          <div role="dialog" aria-modal="true" style={{ backgroundColor: 'var(--eb-surface)', padding: 24, borderRadius: 'var(--eb-radius-xl)', boxShadow: 'var(--eb-shadow-lg)', textAlign: 'center', width: '100%', maxWidth: 420, maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--eb-text)' }}>Votre code de fidélité</h2>
            <p style={{ fontSize: 32, fontWeight: 700, letterSpacing: '0.08em', fontFamily: 'var(--eb-font-mono)', margin: '16px 0', color: 'var(--eb-text)', overflowWrap: 'anywhere' }}>{generatedCode}</p>
            <div style={{ marginBottom: 20, overflowX: 'auto', display: 'flex', justifyContent: 'center' }}><Barcode value={generatedCode} /></div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => navigate('/merchantinvoices')} className="eb-btn eb-btn--primary" style={{ minWidth: 120 }}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantCamera;
